import React, { useRef } from 'react';

interface LogoProps {
  width?: number;
  height?: number;
  className?: string;
  showText?: boolean;
  textClassName?: string;
  downloadable?: boolean;
}

const Logo: React.FC<LogoProps> = ({ 
  width = 40, 
  height = 40, 
  className = '',
  showText = false,
  textClassName = '',
  downloadable = false
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const downloadLogo = async () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    if (!ctx) return;

    // Set canvas size for high quality
    const scale = 4; // Higher scale for better quality
    const logoSize = 512; // Final size in pixels
    canvas.width = logoSize;
    canvas.height = logoSize;

    // Create gradient background
    const gradient = ctx.createLinearGradient(0, 0, logoSize, logoSize);
    gradient.addColorStop(0, '#3B82F6'); // blue-500
    gradient.addColorStop(1, '#9333EA'); // purple-600
    
    // Draw rounded rectangle background
    const borderRadius = logoSize * 0.15;
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.moveTo(borderRadius, 0);
    ctx.lineTo(logoSize - borderRadius, 0);
    ctx.quadraticCurveTo(logoSize, 0, logoSize, borderRadius);
    ctx.lineTo(logoSize, logoSize - borderRadius);
    ctx.quadraticCurveTo(logoSize, logoSize, logoSize - borderRadius, logoSize);
    ctx.lineTo(borderRadius, logoSize);
    ctx.quadraticCurveTo(0, logoSize, 0, logoSize - borderRadius);
    ctx.lineTo(0, borderRadius);
    ctx.quadraticCurveTo(0, 0, borderRadius, 0);
    ctx.closePath();
    ctx.fill();

    // Draw lightbulb icon
    ctx.strokeStyle = 'white';
    ctx.lineWidth = logoSize * 0.04;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    const centerX = logoSize / 2;
    const centerY = logoSize / 2;
    const bulbRadius = logoSize * 0.15;
    
    // Draw bulb shape
    ctx.beginPath();
    ctx.arc(centerX, centerY - bulbRadius * 0.3, bulbRadius, Math.PI * 1.2, Math.PI * -0.2);
    ctx.stroke();
    
    // Draw bulb base
    const baseY = centerY + bulbRadius * 0.7;
    ctx.beginPath();
    ctx.moveTo(centerX - bulbRadius * 0.5, baseY);
    ctx.lineTo(centerX + bulbRadius * 0.5, baseY);
    ctx.stroke();
    
    // Draw filament lines
    ctx.beginPath();
    ctx.moveTo(centerX - bulbRadius * 0.3, baseY + bulbRadius * 0.2);
    ctx.lineTo(centerX - bulbRadius * 0.3, baseY + bulbRadius * 0.4);
    ctx.moveTo(centerX, baseY + bulbRadius * 0.2);
    ctx.lineTo(centerX, baseY + bulbRadius * 0.4);
    ctx.moveTo(centerX + bulbRadius * 0.3, baseY + bulbRadius * 0.2);
    ctx.lineTo(centerX + bulbRadius * 0.3, baseY + bulbRadius * 0.4);
    ctx.stroke();
    
    // Draw light rays
    const rayLength = bulbRadius * 0.4;
    const rays = [
      { angle: -Math.PI / 2, x: 0, y: -1 },
      { angle: -Math.PI / 4, x: 0.707, y: -0.707 },
      { angle: 0, x: 1, y: 0 },
      { angle: Math.PI / 4, x: 0.707, y: 0.707 },
      { angle: -3 * Math.PI / 4, x: -0.707, y: -0.707 },
      { angle: Math.PI, x: -1, y: 0 },
      { angle: 3 * Math.PI / 4, x: -0.707, y: 0.707 }
    ];
    
    rays.forEach(ray => {
      const startX = centerX + (bulbRadius + rayLength * 0.5) * ray.x;
      const startY = centerY - bulbRadius * 0.3 + (bulbRadius + rayLength * 0.5) * ray.y;
      const endX = centerX + (bulbRadius + rayLength * 1.2) * ray.x;
      const endY = centerY - bulbRadius * 0.3 + (bulbRadius + rayLength * 1.2) * ray.y;
      
      ctx.beginPath();
      ctx.moveTo(startX, startY);
      ctx.lineTo(endX, endY);
      ctx.stroke();
    });

    // Convert canvas to blob and download
    canvas.toBlob((blob) => {
      if (blob) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'pm-coach-logo.png';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    }, 'image/png');
  };

  return (
    <div className={`flex items-center ${className}`}>
      <div 
        className={`relative ${downloadable ? 'cursor-pointer hover:scale-105 transition-transform' : ''}`}
        onClick={downloadable ? downloadLogo : undefined}
        title={downloadable ? 'Click to download logo' : undefined}
      >
        <div 
          className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center"
          style={{ width: `${width}px`, height: `${height}px` }}
        >
          <svg 
            className="text-white" 
            width={width * 0.6} 
            height={height * 0.6} 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" 
            />
          </svg>
        </div>
        {downloadable && (
          <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-1 shadow-md">
            <svg className="w-3 h-3 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </div>
        )}
      </div>
      {showText && (
        <span className={`ml-3 text-xl font-bold ${textClassName}`}>PM Coach</span>
      )}
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  );
};

export default Logo;